import * as XLSX from "xlsx";

import { downloadFile } from "utils/file";

const getListOfListsOptimalColumnsWidth = (
  data: Array<Array<string | null | number>>
): Array<{ wch: number }> => {
  /**
   * Calculate the optimal column width for each column by
   * getting the number of symbols in each cell of the column,
   * and calculating the Max value in the column.
   *
   * The result is returned as an array of {wch: number} values where
   * `wch` is the maximum number of symbols in each column.
   *
   * Note: This implementation also works for data without headers row.
   * eg: 3 coulms with headers, but 5 columns with data.
   */
  if (data.length === 0) {
    return [];
  }

  const result: number[] = [];
  data.forEach((row) => {
    row.forEach((cell, index) => {
      if (cell) {
        // The data we receive
        // may contain unexpected types (number, null, undefined, void etc),
        // so we cast them to String to be safe
        const cellLength = String(cell).length;

        // if we already have a width for this item
        if (result[index]) {
          result[index] = Math.max(result[index], cellLength);
        } else {
          result[index] = cellLength;
        }
      }
    });
  });
  return result.map((value) => ({ wch: value }));
};

const setColumnsSettings = (
  sheet: XLSX.WorkSheet,
  settings: XLSX.ColInfo[]
) => {
  /**
   * Set the width of every column in the sheet by providing a list of objects with column settings
   * The setting we use is { wch: number } for every column.
   * Example: `{ wch: 10 }` is a width set by the number of characters. In this case = 10.
   *
   * Reference:
   * https://www.npmjs.com/package/xlsx
   */
  sheet["!cols"] = settings;
};

const listOfListsToWorkSheet = (data: Array<Array<string | null | number>>) => {
  const worksheet = XLSX.utils.aoa_to_sheet(data);

  const colsWidth = getListOfListsOptimalColumnsWidth(data);

  setColumnsSettings(worksheet, colsWidth);

  return worksheet;
};

export const downloadListOfListsAsXLSX = (
  data: Array<Array<string | null | number>>,
  filename: string
) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

  const ws = listOfListsToWorkSheet(data);
  const wb = { Sheets: { Data: ws }, SheetNames: ["Data"] };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: fileType });
  const url = window.URL.createObjectURL(blob);
  downloadFile(url, filename);
};

export const downloadAsXLSXWithMultipleSheets = (
  sheets: Array<{
    sheetName: string;
    data: Array<Array<string | null | number>>;
  }>,
  filename: string
) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

  const worksheets = sheets.reduce((result, sheet) => {
    return { ...result, [sheet.sheetName]: listOfListsToWorkSheet(sheet.data) };
  }, {});

  const wb = {
    Sheets: worksheets,
    SheetNames: sheets.map((sheet) => sheet.sheetName),
  };
  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: fileType });
  const url = window.URL.createObjectURL(blob);
  downloadFile(url, filename);
};
